import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useNewProjectService } from 'domain/shared/newProject';
import { fetchNotificationData } from 'infrastructure/notification/redux/thunks';
import { NotificationItemProps } from 'infrastructure/notification/types';
import useCommercialStateNotifications from './useCommercialStateNotifications';

type NotificationDispatch = ThunkDispatch<unknown, unknown, Action>;

const inFlightDismissIds = new Set<string>();

export const resetDismissedNotificationsGuard = (): void => {
  inFlightDismissIds.clear();
};

const useDismissNotifications = () => {
  const dispatch = useDispatch<NotificationDispatch>();
  const { notificationsDelete } = useNewProjectService();
  const notificationItems = useCommercialStateNotifications();

  const latest = useRef({ notificationsDelete, notificationItems });
  latest.current = { notificationsDelete, notificationItems };

  return useCallback(
    async (ids: string[]) => {
      const freshIds = ids.filter(id => !inFlightDismissIds.has(id));
      if (freshIds.length === 0) return;

      freshIds.forEach(id => inFlightDismissIds.add(id));

      const { notificationsDelete: deleteNotification, notificationItems: items } = latest.current;
      try {
        await Promise.all(freshIds.map(id => deleteNotification(id)));
        await dispatch(fetchNotificationData(items.filter(Boolean) as NotificationItemProps[]));
      } finally {
        freshIds.forEach(id => inFlightDismissIds.delete(id));
      }
    },
    [dispatch],
  );
};

export default useDismissNotifications;
